/* eslint-disable zeffy-linter-frontend/no-classname-prop -- legacy baseline (pre-rule); remove when these are migrated */
import { FC, useCallback, useEffect, useMemo, useState } from "react";

import { Box, keyframes } from "@mui/material";
import { FormType, isBankDebitPaymentMethod, PaymentMethod } from "@simplyk/common";
import Head from "next/head";

import { AnimatedCaption } from "./AnimatedCaption/AnimatedCaption";
import { TIME_BETWEEN_STEPS } from "./helpers";

import { DecorativeLottie } from "@/common/components/DecorativeLottie/DecorativeLottie";
import { CONFIRMATION_SOUND_PATH, preloadConfirmationSound } from "@/canvas/features/ThankYouPage/useConfirmationSound";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { DashboardZeffyLoader } from "@/dashboard-design-system/ZeffyLoader/DashboardZeffyLoader";

const TIME_FADE = 0.5;

const fadeOutKeyframes = keyframes`
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`;

const fadeOutSlideKeyframes = keyframes`
  0% {
    opacity: 1;
    transform: translateX(0);
  }
  100% {
    opacity: 0;
    transform: translateX(-40px);
  }
`;

const fadeInSlideKeyframes = keyframes`
  0% {
    opacity: 0;
    transform: translateX(40px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
`;

export interface ThankYouLoaderProps {
  organizationName: string;
  formType: FormType;
  generateTaxReceipt: boolean;
  generateTicket: boolean;
  isFree: boolean;
  hasADate: boolean;
  paymentMethod?: PaymentMethod | null;
  isAuction: boolean;
  isEntry?: boolean;
  donorFirstName?: string | null;
  donorEmail?: string | null;
  amount?: string | null;
  taxReceiptAmount?: string | null;
  eventName?: string | null;
  eventDate?: string | null;
}

enum STEPS {
  DELIVERY_MONEY = "DELIVERY_MONEY",
  PRINTER = "PRINTER",
  INBOX = "INBOX",
}

type CaptionContent = { highlights: string[]; text: string };

// The printer step has one of these semantic variants; everything else (which Lottie to load, which
// caption to show) is derived from it instead of branching on i18n message keys.
type PrinterVariant = "calendar" | "taxReceipt" | "ticket" | "transaction";

const PRINTER_ANIMATION_BY_VARIANT: Record<PrinterVariant, "printer_calendar" | "printer_receipt" | "printer_tickets"> =
  {
    taxReceipt: "printer_receipt",
    transaction: "printer_receipt",
    ticket: "printer_tickets",
    calendar: "printer_calendar",
  };

export const ThankYouLoader: FC<ThankYouLoaderProps> = ({
  organizationName,
  formType,
  generateTaxReceipt,
  generateTicket,
  isFree,
  hasADate,
  paymentMethod,
  isAuction,
  isEntry = false,
  donorFirstName,
  donorEmail,
  amount,
  taxReceiptAmount,
  eventName,
  eventDate,
}) => {
  preloadConfirmationSound();

  const { t } = useTranslate();
  const { isSmallScreen } = useMediaQuery();
  const [step, setStep] = useState(isFree && !hasADate ? STEPS.INBOX : isFree ? STEPS.PRINTER : STEPS.DELIVERY_MONEY);
  const [fade, setFade] = useState("fade-in");
  const [isLoadingAnimation, setIsLoadingAnimation] = useState(true);
  const [thankYouPageAnimationDataDeliveryMoney, setThankYouPageAnimationDataDeliveryMoney] = useState<unknown | null>(
    null
  );
  const [thankYouPageAnimationDataPrinter, setThankYouPageAnimationDataPrinter] = useState<unknown | null>(null);
  const [thankYouPageAnimationDataEmail, setThankYouPageAnimationDataEmail] = useState<unknown | null>(null);
  const paymentMethodIsAchOrPad = isBankDebitPaymentMethod(paymentMethod);
  const isTaxReceiptGeneratedNow = generateTaxReceipt && paymentMethod !== PaymentMethod.Cheque;

  const steps = useMemo(
    () =>
      Object.values(STEPS).filter(
        (value) =>
          !(isFree && value === STEPS.DELIVERY_MONEY) &&
          !(isFree && !hasADate && value === STEPS.PRINTER) &&
          !(paymentMethodIsAchOrPad && value === STEPS.PRINTER) &&
          !(isAuction && value === STEPS.PRINTER)
      ),
    [hasADate, isAuction, isFree, paymentMethodIsAchOrPad]
  );

  useEffect(() => {
    let currentIndex = 0;

    const interval = setInterval(() => {
      setFade("fade-out");

      setTimeout(() => {
        currentIndex = (currentIndex + 1) % steps.length;
        setStep(steps[currentIndex]);
        setFade("fade-in");
      }, TIME_FADE * 1000);
    }, TIME_BETWEEN_STEPS * 1000);

    return () => clearInterval(interval);
  }, [steps]);

  const getPrinterVariant = useCallback((): PrinterVariant => {
    if (formType === FormType.DonationForm) {
      return isTaxReceiptGeneratedNow ? "taxReceipt" : "transaction";
    }
    if (generateTicket) {
      return "ticket";
    }
    if (isTaxReceiptGeneratedNow) {
      return "taxReceipt";
    }
    if (hasADate && isFree) {
      return "calendar";
    }
    return "transaction";
  }, [formType, isTaxReceiptGeneratedNow, generateTicket, hasADate, isFree]);

  useEffect(() => {
    const fetchThankYouPageAnimation = async () => {
      const printerAnimationType = PRINTER_ANIMATION_BY_VARIANT[getPrinterVariant()];
      const [animationDataDeliveryMoney, animationDataPrinter, animationDataEmail] = await Promise.all([
        import("./animations/delivery_money.json"),
        printerAnimationType === "printer_receipt"
          ? import("./animations/printer_receipt.json")
          : printerAnimationType === "printer_tickets"
            ? import("./animations/printer_tickets.json")
            : import("./animations/printer_calendar.json"),
        import("./animations/inbox.json"),
      ]);

      setThankYouPageAnimationDataDeliveryMoney(animationDataDeliveryMoney.default);
      setThankYouPageAnimationDataPrinter(animationDataPrinter.default);
      setThankYouPageAnimationDataEmail(animationDataEmail.default);
      setIsLoadingAnimation(false);
    };
    fetchThankYouPageAnimation();
  }, [getPrinterVariant, organizationName]);

  const getDeliveryCaption = useCallback((): CaptionContent => {
    if (paymentMethodIsAchOrPad) {
      return {
        text: t("common", "thankYouLoader.initiatingTransfer.ach", {
          donorName: donorFirstName,
          orgName: organizationName,
        }),
        highlights: donorFirstName ? [donorFirstName] : [],
      };
    }
    if (isAuction) {
      return {
        text: t("common", "thankYouLoader.deliveryMoney.auction", {
          donorName: donorFirstName,
          orgName: organizationName,
        }),
        highlights: donorFirstName ? [donorFirstName] : [],
      };
    }
    const isDonation = formType === FormType.DonationForm;
    if (donorFirstName) {
      return {
        text: t(
          "common",
          isDonation ? "thankYouLoader.caption.delivery.donation" : "thankYouLoader.caption.delivery.ticketing",
          { donorName: donorFirstName, orgName: organizationName }
        ),
        highlights: [donorFirstName],
      };
    }
    return {
      text: t(
        "common",
        isDonation ? "thankYouLoader.deliveryMoney.donation" : "thankYouLoader.deliveryMoney.ticketing",
        { orgName: organizationName }
      ),
      highlights: [],
    };
  }, [paymentMethodIsAchOrPad, isAuction, formType, donorFirstName, organizationName, t]);

  const getPrinterCaption = useCallback((): CaptionContent => {
    const printerVariant = getPrinterVariant();
    if (printerVariant === "taxReceipt") {
      return taxReceiptAmount
        ? {
            text: t("common", "thankYouLoader.caption.receipt", { amount: taxReceiptAmount }),
            highlights: [taxReceiptAmount],
          }
        : { text: t("common", "thankYouLoader.caption.receipt.noAmount"), highlights: [] };
    }
    if (printerVariant === "ticket") {
      return eventName
        ? {
            text: t("common", isEntry ? "thankYouLoader.caption.entries" : "thankYouLoader.caption.tickets", {
              eventName,
            }),
            highlights: [eventName],
          }
        : {
            text: t(
              "common",
              isEntry ? "thankYouLoader.caption.entries.noEventName" : "thankYouLoader.caption.tickets.noEventName"
            ),
            highlights: [],
          };
    }
    if (printerVariant === "calendar") {
      return eventDate
        ? { text: t("common", "thankYouLoader.caption.calendar", { eventDate }), highlights: [eventDate] }
        : { text: t("common", "thankYouLoader.caption.calendar.noEventDate"), highlights: [] };
    }
    return amount
      ? { text: t("common", "thankYouLoader.caption.transaction", { amount }), highlights: [amount] }
      : { text: t("common", "thankYouLoader.caption.transaction.noAmount"), highlights: [] };
  }, [getPrinterVariant, amount, taxReceiptAmount, eventName, eventDate, isEntry, t]);

  const getInboxCaption = useCallback((): CaptionContent => {
    if (!donorEmail) {
      return {
        text: t(
          "common",
          paymentMethodIsAchOrPad
            ? "thankYouLoader.sendingEmail.ach"
            : isAuction
              ? "thankYouLoader.sendingEmail.auction"
              : steps.length === 1
                ? "thankYouLoader.email.only-one-animation"
                : "thankYouLoader.email"
        ),
        highlights: [],
      };
    }
    const inboxKey = paymentMethodIsAchOrPad
      ? "thankYouLoader.caption.inbox.transfer"
      : isAuction || steps.length === 1
        ? "thankYouLoader.caption.inbox.confirmation"
        : "thankYouLoader.caption.inbox";
    return { text: t("common", inboxKey, { email: donorEmail }), highlights: [donorEmail] };
  }, [donorEmail, paymentMethodIsAchOrPad, isAuction, steps.length, t]);

  const getCaptionContent = useCallback(
    (currentStep: STEPS): CaptionContent => {
      switch (currentStep) {
        case STEPS.DELIVERY_MONEY:
          return getDeliveryCaption();
        case STEPS.PRINTER:
          return getPrinterCaption();
        case STEPS.INBOX:
          return getInboxCaption();
        default:
          return { text: "", highlights: [] };
      }
    },
    [getDeliveryCaption, getPrinterCaption, getInboxCaption]
  );

  const renderAnimation = useCallback(
    (animationData: unknown, caption: { text: string; highlights: string[] }) => (
      <>
        <Box aria-hidden sx={{ width: isSmallScreen ? "100%" : "60%", margin: "0 auto" }}>
          <DecorativeLottie
            options={{
              animationData,
              autoplay: true,
              loop: true,
            }}
            isClickToPauseDisabled={true}
            style={{ width: "100%" }}
          />
        </Box>
        <AnimatedCaption key={step} text={caption.text} highlights={caption.highlights} />
      </>
    ),
    [isSmallScreen, step]
  );

  const animation = useMemo(() => {
    const caption = getCaptionContent(step);
    switch (step) {
      case STEPS.DELIVERY_MONEY:
        return renderAnimation(thankYouPageAnimationDataDeliveryMoney, caption);
      case STEPS.PRINTER:
        return renderAnimation(thankYouPageAnimationDataPrinter, caption);
      case STEPS.INBOX:
        return renderAnimation(thankYouPageAnimationDataEmail, caption);
    }
  }, [
    getCaptionContent,
    step,
    renderAnimation,
    thankYouPageAnimationDataDeliveryMoney,
    thankYouPageAnimationDataPrinter,
    thankYouPageAnimationDataEmail,
  ]);

  return (
    <Box
      sx={{
        margin: "auto",
        maxWidth: "100%",
        "&.fade-out": {
          animation: `${fadeOutKeyframes} 0.3s ease-in-out forwards`,
        },
      }}
      data-test="thank-you-loader"
    >
      <Head>
        <link rel="prefetch" href={CONFIRMATION_SOUND_PATH} as="fetch" />
      </Head>
      {isLoadingAnimation ? (
        <DashboardZeffyLoader />
      ) : (
        <Box
          className={fade}
          sx={{
            "&.fade-out": {
              animation: `${fadeOutSlideKeyframes} ${TIME_FADE}s ease-in-out forwards`,
            },
            "&.fade-in": {
              animation: `${fadeInSlideKeyframes} ${TIME_FADE}s ease-in-out forwards`,
            },
          }}
        >
          {animation}
        </Box>
      )}
    </Box>
  );
};
